/**
 * @file modal-mixin.ts
 *
 * Owns the as-modal-overlay reference and modal content state. Provides three
 * modal entry points used throughout the app:
 *
 *   showModal()      — renders a transient panel (mesh transfer, autoload, etc.)
 *                      from modalContent and cleans up via the 'as-modal-closed'
 *                      listener when the overlay closes.
 *   openModal()      — opens a named, persistent panel by id/tag, creating the
 *                      element if it does not yet exist.
 *   showErrorModal() — surfaces the standing error panel.
 *
 * Exposes: modalOverlay, modalContent, isHomeRedirectPending,
 *          showModal, openModal, showErrorModal
 */
import { AsModalOverlay } from '../../overlays/as-modal-overlay.ts';
import { type ModalContent } from '../modal-templates.ts';
import * as ModalHandlers from '../app-modal.ts';
import { type Constructor } from './mixin-types-utils.ts';

/** Surface added by ModalMixin. Referenced by ErrorMixin (showErrorModal),
 *  ProjectMixin (openModal), and MeshMixin (showModal, showErrorModal). */
export interface ModalMixinMembers {
  modalOverlay: AsModalOverlay | null;
  /** Current transient modal data; type 'none' means no transient panel. */
  modalContent: ModalContent;
  /** Set while the home-redirect warning modal is open to track pending nav. */
  isHomeRedirectPending: boolean;
  /** Renders modalContent into the overlay and opens it. */
  showModal(): void;
  /** Opens a named persistent panel by id, creating it if absent. */
  openModal(modalId: string, tagName: string, attributes?: Record<string, string>): void;
  /** Surfaces the standing error panel with the given error detail. */
  showErrorModal(detail: { error: Error | string; origin?: string; info?: string }): void;
}

export const ModalMixin = <T extends Constructor<HTMLElement>>(Base: T) =>
  class ModalMixin extends Base {
    modalOverlay: AsModalOverlay | null = null;
    modalContent: ModalContent = { type: 'none' };
    isHomeRedirectPending: boolean = false;

    private _handleModalClosed = (): void => {
      ModalHandlers.onModalClosed(this as any);
      this.modalOverlay?.removeEventListener('as-modal-closed', this._handleModalClosed);
    };

    async connectedCallback() {
      await super.connectedCallback?.();
      this.modalOverlay = (this.shadowRoot?.getElementById('as-app-overlay') ?? null) as AsModalOverlay | null;
    }

    disconnectedCallback() {
      super.disconnectedCallback?.();
    }

    public showModal(): void {
      ModalHandlers.showModal(this as any);
      this.modalOverlay?.addEventListener('as-modal-closed', this._handleModalClosed);
    }

    public openModal(modalId: string, tagName: string, attributes?: Record<string, string>): void {
      ModalHandlers.openModal(this as any, modalId, tagName, attributes);
    }

    public showErrorModal(detail: { error: Error | string; origin?: string; info?: string }): void {
      ModalHandlers.showErrorModal(this as any, detail);
    }
  };
